import React from "react";
import { Typography, Button, Alert } from "antd";
import { WhatsAppOutlined, CreditCardOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Card from "../../../../Components/ui/Card";

const { Title, Paragraph } = Typography;

const BlockerUI = ({ isWhatsappConnected, hasSubscription }) => {
  const navigate = useNavigate();

  return (
    <div className="blocker-container">
      <Card className="blocker-card" padding="var(--spacing-8)">
        <div className="blocker-header">
          <Title level={3}>
            {!isWhatsappConnected ? "Connect WhatsApp" : "Activate Subscription"}
          </Title>
          <Paragraph type="secondary">
            {!isWhatsappConnected
              ? "To access the platform, please connect your WhatsApp account first."
              : "You need an active subscription to continue using the platform."}
          </Paragraph>
        </div>

        <div className="status-grid">
          <StatusItem
            icon={<CheckCircleOutlined />}
            fallbackIcon={<WhatsAppOutlined />}
            active={isWhatsappConnected}
            title="WhatsApp Connection"
            statusText={isWhatsappConnected ? "Connected" : "Not Connected"}
          />
          <StatusItem
            icon={<CheckCircleOutlined />}
            fallbackIcon={<CreditCardOutlined />}
            active={hasSubscription}
            title="Subscription"
            statusText={hasSubscription ? "Active" : "No Active Subscription"}
          />
        </div>

        <div className="blocker-actions">
          {!isWhatsappConnected ? (
            <Button
              type="primary"
              size="large"
              icon={<WhatsAppOutlined />}
              onClick={() => navigate("/admin/whatsapp-connect")}
              block
              className="premium-btn whatsapp-btn"
            >
              Connect WhatsApp
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              icon={<CreditCardOutlined />}
              onClick={() => navigate("/admin/browse-plans")}
              block
              className="premium-btn"
            >
              View Pricing Plans
            </Button>
          )}
        </div>

        <Alert
          message="Setup Required"
          description={
            !isWhatsappConnected
              ? "Connect your WhatsApp account to start sending campaigns and using automation features."
              : "Choose a subscription plan that fits your needs to unlock all features."
          }
          type="info"
          showIcon
          className="blocker-alert"
        />
      </Card>

      <style jsx>{`
        .blocker-container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: calc(100vh - 120px);
          padding: 24px;
        }

        .blocker-card {
          max-width: 520px;
          width: 100%;
          text-align: center;
        }

        .blocker-header {
          margin-bottom: 32px;
        }

        .status-grid {
          display: grid;
          gap: 16px;
          margin-bottom: 32px;
        }

        .blocker-actions {
          margin-bottom: 24px;
        }

        .premium-btn {
          height: 52px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
        }

        .whatsapp-btn {
          background: #25D366;
          border-color: #25D366;
        }

        .whatsapp-btn:hover {
          background: #22c35e !important;
          border-color: #22c35e !important;
        }

        .blocker-alert {
          text-align: left;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

const StatusItem = ({ active, icon, fallbackIcon, title, statusText }) => (
  <div className={`status-item ${active ? 'active' : 'inactive'}`}>
    <div className="status-icon">
      {active ? icon : fallbackIcon}
    </div>
    <div className="status-info">
      <span className="status-title">{title}</span>
      <span className="status-label">{statusText}</span>
    </div>
    <style jsx>{`
      .status-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        border-radius: 12px;
        text-align: left;
        border: 1px solid var(--border-color);
        transition: all 0.2s ease;
      }

      .status-item.active {
        background: rgba(16, 185, 129, 0.04);
        border-color: rgba(16, 185, 129, 0.2);
      }

      .status-item.inactive {
        background: rgba(239, 68, 68, 0.04);
        border-color: rgba(239, 68, 68, 0.2);
      }

      .status-icon {
        font-size: 24px;
        display: flex;
        color: ${active ? '#10b981' : '#ef4444'};
      }

      .status-info {
        display: flex;
        flex-direction: column;
      }

      .status-title {
        font-weight: 500;
        color: var(--text-primary);
        font-size: 14px;
      }

      .status-label {
        font-size: 12px;
        color: var(--text-secondary);
      }
    `}</style>
  </div>
);

export default BlockerUI;
